import './Match.less';

import React, { FC, memo, ReactNode, useEffect, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { hasMarketBet$$, match$$, showMatch$$ } from '../push/overview/subs';
import OverviewBet from './OverviewBet';
import { hasGameScorePlaceholder, isHalfTime, localizeMap } from '../utils';
import { FavoriteIcon, MatchIcons, MatchIconsCols, StatsIcon } from './MatchIcons';
import { formatStartTime } from '../timer';
import { generatePath, Link, useHistory, useRouteMatch } from 'react-router-dom';
import clsx from 'clsx';
import { TURBO_LIVE_MATCH_DETAIL_PATH, TURBO_LIVE_SPORT_PATH } from '../../../common/routing';
import { SvgIcon } from '../../../icons/SvgIcon';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { setOverviewScrollY } from '../handlers';
import { overviewScrollY } from './SportOverview';
import { useFavoriteMatch } from '../favorite/hooks';
import { useTurboLiveRouteParams } from '../routes';
import { useSharedIntersectionObserver } from '../../../hooks/shared-intersection-observer/hook';
import { CashinMatchCollapsible, CashinMatchToggle } from './cashin/CashinOffersList';
import { getScoreboardType, isRainDelayExtra } from '../matchExtras';
import { MatchTimer } from './MatchTimer';
import { loggedIn$ } from '../subs';
import { TicketsListMatchToggle, TicketsMatchCollapsible } from './tickets/TicketsList';
import { ToggleContainer } from './ToggleBtn';
import { ErrorBoundary } from '../../../common/ErrorBoundary';
import i18n from '../../../common/i18n';
import useMarketIds from '../hooks/useMarketIds';
import useTimeout from '../../../common/hooks/useTimeout';
import { isMobileChannel, showGoalAnimation } from '../../../common/util';
import { usePrevious } from '../../../common/hooks/usePreviousValue';
import { CashinPosition } from './cashin/constants';
import CashinPositionContext from '../../../common/CashinPositionContext';

export const isShortTimeFormat = sportName => sportName === 'futbal';

export const MatchLiveSign: FC<{ startTime: any; otherClass?: string }> = ({ startTime, otherClass }) => {
    const title = `${i18n('nui.turbo.live.match.start')} ${formatStartTime(startTime)}`;
    return <SvgIcon imgId="live" sizeX="25" sizeY="14" title={title} className={clsx('fs-0 c-orange', otherClass)} />;
};

const PrevScore: FC<{ score: number }> = memo(({ score }) => {
    const prevScore = usePrevious(score);
    const scoreChanged = prevScore && prevScore < score;
    return scoreChanged ? <PrevScoreView key={`prev${prevScore}`} prevScore={--score} /> : null;
});

const PrevScoreView: FC<{ prevScore: number }> = ({ prevScore }) => {
    const [visible, setVisible] = useState(true);
    useTimeout(() => setVisible(false), 1000);
    return visible ? <span className="match-prev-score p-abs">{prevScore}</span> : null;
};

const MatchScore: FC<{
    score: any;
    server?: string;
    otherClass?: string;
    dataAtid: string;
    ticketScoreboard?: boolean;
    showAnimation?: boolean;
    hasPlaceholder?: boolean;
}> = memo(({ score, otherClass, dataAtid, ticketScoreboard, server, showAnimation, hasPlaceholder }) => {
    if (!score || score.size === 0) {
        return hasPlaceholder ? <span className="match-score-col" /> : null;
    }
    const scoreCssClass = () =>
        `flex justify-center text-right flex-col match-score-col fs-12 ${otherClass || 'c-black-50'}`;
    const scoreRowClass = clsx({ 'py-2': isMobileChannel() && !ticketScoreboard, 'p-rel': showAnimation });
    const homeScore = score.get('home');
    const awayScore = score.get('away');
    return (
        <div className={scoreCssClass()} data-atid={`tlv-overview-${dataAtid}`}>
            <span className={scoreRowClass} data-atid={`tlv-overview-${dataAtid}-home`}>
                {server === 'HOME' && <MatchServer />}
                {homeScore}
                {showAnimation && <PrevScore score={homeScore} />}
            </span>
            <span className={scoreRowClass} data-atid={`tlv-overview-${dataAtid}-away`}>
                {server === 'AWAY' && <MatchServer />}
                {awayScore}
                {showAnimation && <PrevScore score={awayScore} />}
            </span>
        </div>
    );
});

const MatchServer: FC = () => (
    <div className="match-serve-l d-inline-block" title={i18n('nui.turbo.live.match.server')} />
);

const MatchPeriodScores = ({ periodScores, sportId, ticketScoreboard }) => {
    return (
        <>
            {(sportId === 85 || sportId === 88 || sportId === 59) && periodScores?.size > 1 ? (
                <MatchScore
                    score={periodScores.getIn([periodScores.size - 1, 'score'])}
                    otherClass="text-extra-bold c-black-50"
                    dataAtid={'periodScore'}
                    ticketScoreboard={ticketScoreboard}
                />
            ) : (
                periodScores.map((period, i) => {
                    const isHalfTimePeriod = isHalfTime(period.get('periodType'));

                    return period ? (
                        <MatchScore
                            score={period.get('score')}
                            key={i}
                            otherClass={clsx({
                                'text-extra-bold c-black-50': periodScores.size - 1 === i,
                                'ml-5 br-default br-radius-xxxs c-black-50': isHalfTimePeriod,
                                'halftime-scoreboard pr-5': isHalfTimePeriod && ticketScoreboard,
                                'px-3 my-5': isHalfTimePeriod && !ticketScoreboard
                            })}
                            dataAtid={'periodScore'}
                            ticketScoreboard={ticketScoreboard}
                        />
                    ) : null;
                })
            )}
        </>
    );
};

export const MatchScoreboard: FC<{
    score: any;
    server?: string;
    sportId: number;
    ticketScoreboard?: boolean;
    hasScorePlaceholder?: boolean;
}> = ({ score, server, sportId, ticketScoreboard, hasScorePlaceholder }) => {
    const scores = score?.get('scores');
    if (!scores?.size) {
        return null;
    }
    const gameScore = scores.get('GAME');
    const showAnimation = showGoalAnimation(sportId);
    return (
        <div
            className={clsx('flex mr-10 f-condensed', {
                'lh-1-5 items-end': ticketScoreboard
            })}
        >
            <MatchScore
                score={scores.get('TOTAL')}
                server={server}
                otherClass="c-orange text-extra-bold"
                dataAtid={'totalScore'}
                ticketScoreboard={ticketScoreboard}
                showAnimation={showAnimation}
            />
            {!ticketScoreboard && sportId !== 85 && sportId !== 88 && sportId !== 59 ? (
                <MatchScore
                    score={scores.get('PERIOD')}
                    otherClass="text-extra-bold c-black-50"
                    dataAtid={'periodScore'}
                    ticketScoreboard={ticketScoreboard}
                />
            ) : (
                <MatchPeriodScores
                    periodScores={score.get('periodScores')}
                    sportId={sportId}
                    ticketScoreboard={ticketScoreboard}
                />
            )}
            <MatchScore
                score={gameScore}
                otherClass={clsx('c-black-50', {
                    'match-game italic c-orange ml-auto': gameScore && !ticketScoreboard
                })}
                dataAtid={'periodScore'}
                ticketScoreboard={ticketScoreboard}
                hasPlaceholder={hasScorePlaceholder}
            />
        </div>
    );
};

const MatchBets: FC<{
    matchId;
    betCount?: number;
    otherClass?;
    sportName;
    status: string;
}> = ({ matchId, otherClass, sportName, betCount, status }) => {
    const derefSub = useReframe();
    const selectedMarketIds = useMarketIds(sportName);
    const betsCols = selectedMarketIds?.size;
    if (!betsCols) {
        return null;
    }
    const haveSelectedMarketsBets = selectedMarketIds
        .map(marketId => derefSub(hasMarketBet$$(sportName, matchId, marketId)))
        .filter(id => !!id);

    if (status === 'FINISHED') {
        return (
            <MatchBetsInfo
                i18nKey="nui.turbo.live.finishedEvent"
                cols={betsCols}
                matchId={matchId}
                sportName={sportName}
            />
        );
    } else if (status === 'SCHEDULED' && betCount === 0) {
        return (
            <MatchBetsInfo
                i18nKey="nui.turbo.live.oddsAvailableSoon"
                cols={betsCols}
                matchId={matchId}
                sportName={sportName}
            />
        );
    } else if (haveSelectedMarketsBets.size === 0 && betCount !== 0) {
        return (
            <MatchBetsInfo
                i18nKey="nui.turbo.live.seeOtherOdds"
                cols={betsCols}
                matchId={matchId}
                sportName={sportName}
                linkToDetail
            />
        );
    } else if (betCount === 0 && ['IN_PROGRESS', 'INTERRUPTED', 'BREAK', 'CANCELLED'].includes(status)) {
        return (
            <MatchBetsInfo
                i18nKey="nui.turbo.live.noBettingEvents"
                cols={betsCols}
                matchId={matchId}
                sportName={sportName}
            />
        );
    }

    return (
        <ErrorBoundary description="TurboLive MatchBets">
            {selectedMarketIds.map((marketId, i) => (
                <OverviewBet
                    sportCode={sportName}
                    matchId={matchId}
                    marketId={marketId}
                    key={i}
                    columnId={i}
                    otherClass={otherClass}
                />
            ))}
        </ErrorBoundary>
    );
};

const MatchBetsInfo: FC<{
    i18nKey: string;
    cols: number;
    matchId: string;
    sportName: string;
    linkToDetail?: boolean;
}> = ({ i18nKey, cols, matchId, sportName, linkToDetail = false }) => {
    const matchMedia = useMatchMedia();
    const isMobile = isMobileChannel();
    return (
        <>
            {Array.from({ length: cols }).map((e, i) => (
                <div
                    key={i}
                    className={clsx('flex flex-1 turbo-live-col match-bets-info', {
                        'no-wrap': cols > 1 && !linkToDetail,
                        'pl-20': i !== 0 && isMobile,
                        'tl-br-l': i === 0
                    })}
                >
                    {i === 0 &&
                        (linkToDetail ? (
                            <MatchDetailBtn
                                matchId={matchId}
                                sportName={sportName}
                                otherClass={clsx('flex flex-1 flex-center px-10 bg-black-5 tl-br-r', 'flex-gap-10')}
                            >
                                <span
                                    className={clsx('c-black-50 text-center m-0', {
                                        'fs-11': !matchMedia.xxl && !isMobile,
                                        'fs-13': matchMedia.xxl || isMobile
                                    })}
                                    data-atid="no-odds-at-the-moment"
                                >
                                    {i18n(i18nKey)}
                                </span>
                                <SvgIcon
                                    imgId="live-see-other-odds"
                                    sizeX={'16'}
                                    sizeY={'16'}
                                    className="flex-shrink-0"
                                />
                            </MatchDetailBtn>
                        ) : (
                            <span
                                className="flex items-center px-15 c-black-50 fs-13"
                                data-atid="no-odds-at-the-moment"
                            >
                                {i18n(i18nKey)}
                            </span>
                        ))}
                </div>
            ))}
        </>
    );
};

export const MatchDetailBtn: FC<{ matchId: string; sportName; otherClass?: string; children: any }> = ({
    matchId,
    sportName,
    otherClass,
    children
}) => {
    const path = generatePath(TURBO_LIVE_MATCH_DETAIL_PATH, { sportName, matchId });
    const history = useHistory();
    const onClick = e => {
        e.preventDefault();
        const scrollY = overviewScrollY();
        history.push(path);
        dispatch(setOverviewScrollY(scrollY));
    };
    return (
        <Link to={path} onClick={e => onClick(e)} className={clsx(otherClass)}>
            {children}
        </Link>
    );
};

const MatchOpponent = memo(
    ({ name, score, side, children }: { name: string; score: number; side: string; children?: ReactNode }) => {
        const prevScore = usePrevious(score);
        const goal = prevScore && prevScore < score;
        const cssClass = clsx('ellipsis f-condensed c-black-100 text-extra-bold pl-10 pr-2', {
            'py-2': isMobileChannel(),
            'match-opponent-goal': goal
        });
        return (
            <>
                <span key={`${side}-${score}`} className={cssClass} title={name} data-atid={`match-opponents-${side}`}>
                    {name}
                </span>
                {children}
                {goal && <MatchOpponentGoal key={`${side}-${score}-g`} />}
            </>
        );
    }
);

const MatchOpponentGoal: FC = () => {
    const [shouldDestroy, setShouldDestroy] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setShouldDestroy(true);
        }, 13000);

        return () => clearTimeout(timeout); // Cleanup the
    }, []);

    return shouldDestroy ? null : (
        <span className="match-opponent-goal p-rel match-goal" data-goal={i18n('nui.turbo.live.gol')} />
    );
};

export const MatchOpponents: FC<{ home: string; away: string; stats?: any; mainScore?: any }> = ({
    home,
    away,
    stats,
    mainScore
}) => {
    const cards = stats?.get('RED_CARDS');
    const penalties = stats?.get('SUSPENDS');
    const homeStats = parseInt(penalties?.split(':')[0]) || parseInt(cards?.split(':')[0]);
    const awayStats = parseInt(penalties?.split(':')[1]) || parseInt(cards?.split(':')[1]);
    const statsClass = clsx('flex justify-start items-center', homeStats || awayStats ? 'p-rel' : '');
    let titleExplain = '';
    if (cards) {
        titleExplain = i18n('nui.turbo.live.match.redCards');
    } else if (penalties) {
        titleExplain = i18n('nui.turbo.live.match.penalties');
    }
    const homeScore = mainScore && mainScore.get('home');
    const awayScore = mainScore && mainScore?.get('away');
    return (
        <div className="flex flex-col justify-center flex-1 fs-12 pr-2">
            <div className={statsClass}>
                <MatchOpponent name={home} score={homeScore} side="home">
                    {homeStats > 0 && (
                        <span
                            className={clsx('flex-shrink-0', {
                                'match-penalties': penalties && homeStats,
                                'match-cards flex-center': cards && homeStats
                            })}
                            title={titleExplain}
                        >
                            {homeStats}
                        </span>
                    )}
                </MatchOpponent>
            </div>
            <div className={statsClass}>
                <MatchOpponent name={away} score={awayScore} side="away">
                    {awayStats > 0 && (
                        <span
                            className={clsx('flex-shrink-0', {
                                'match-penalties': penalties && awayStats,
                                'match-cards flex-center': cards && awayStats
                            })}
                            title={titleExplain}
                        >
                            {awayStats}
                        </span>
                    )}
                </MatchOpponent>
            </div>
        </div>
    );
};

const MatchBetCount: FC<{ betCount?; matchId: string; sportName: string }> = ({ betCount, matchId, sportName }) =>
    isMobileChannel() ? (
        <span
            className="match-bet-count c-black-30 text-right fs-12 pr-10 "
            data-atid={betCount ? 'match-bet-count' : ''}
        >
            {betCount ? `+${betCount}` : null}
        </span>
    ) : betCount ? (
        <MatchDetailBtn
            matchId={matchId}
            sportName={sportName}
            otherClass="match-more c-black-25 text-right fs-12 pr-10"
        >
            {`+${betCount}`}
        </MatchDetailBtn>
    ) : (
        <span className="pr-10" />
    );

export const MatchMini: FC<{ matchId: string; sportName?: string }> = memo(({ matchId, sportName }) => {
    const derefSub = useReframe();
    const loggedIn = derefSub(loggedIn$);
    const match = derefSub(match$$(sportName, matchId));
    const showMatch = derefSub(showMatch$$(sportName, matchId));
    const [ref, isVisible] = useSharedIntersectionObserver();
    const matchMedia = useMatchMedia();
    const { isFavorite, toggleFavorite } = useFavoriteMatch(sportName, match);
    if (!match || !showMatch) {
        return null;
    }
    const home = localizeMap(match.get('home'));
    const away = localizeMap(match.get('away'));
    const betCount = match.get('betCount');
    const matchTimer = match.get('timer');
    const status = matchTimer?.get('status');
    const score = match.get('score');
    const server = score?.get('server');
    const stats = match.get('stats');
    const isRainDelay = isRainDelayExtra(match);
    const sportId = parseInt(match.get('sportId'));
    const singleOpponent = !match.get('away');
    const statisticsId = match.get('statisticsId');
    const matchSm = matchMedia.sm;
    const mainScore = showGoalAnimation(sportId) && score?.getIn(['scores', 'TOTAL']);

    const matchTimerExtra = (
        <>
            <MatchTimer
                scoreboardTypeId={getScoreboardType(match)}
                matchTimer={matchTimer}
                otherClass="fs-12 c-black-50"
                shortTimeFormat={isShortTimeFormat(sportName)}
                mini
            />
            {isRainDelay && (
                <SvgIcon
                    imgId="real-cloud"
                    sizeX="14"
                    sizeY="14"
                    className="ml-10 c-red"
                    title={i18n('nui.turbo.live.match.rainDelay')}
                />
            )}
        </>
    );

    const matchMiniIcons = (
        <>
            {loggedIn && <CashinMatchToggle matchId={matchId} />}
            {loggedIn && <TicketsListMatchToggle matchId={matchId} />}
            <MatchIcons match={match} sportName={sportName} />
        </>
    );

    return (
        <div ref={ref} className="match-borders match-mini-separate bg-white-box">
            <MatchDetailBtn
                matchId={matchId}
                sportName={sportName}
                otherClass="match-mini-header flex items-center br-b-default-light"
            >
                {isVisible && (
                    <FavoriteIcon
                        active={isFavorite}
                        toggle={toggleFavorite}
                        iconClass="mx-10"
                        data-atid="match-favorite-icon"
                    />
                )}
                {matchSm ? (
                    matchTimerExtra
                ) : (
                    <div className="flex-1 self-stretch flex flex-col justify-evenly">
                        <div className="flex items-center">
                            <div className="flex-1 flex justify-end items-center">{matchMiniIcons}</div>
                        </div>
                        <div className="ml-10 flex">{matchTimerExtra}</div>
                    </div>
                )}

                {isVisible && (
                    <div className="flex-1 flex items-center justify-end">
                        {matchSm && matchMiniIcons}

                        {statisticsId ? (
                            <SvgIcon imgId="live-stats" className="mx-10 c-black-25 my-10" sizeX="16" sizeY="16" />
                        ) : (
                            <span className="px-15 py-15 mr-3" />
                        )}

                        <MatchBetCount matchId={matchId} sportName={sportName} betCount={betCount} />
                    </div>
                )}
            </MatchDetailBtn>
            <MatchDetailBtn matchId={matchId} sportName={sportName} otherClass="match-live match-mini flex">
                <div className="flex items-center turbo-live-col ellipsis">
                    <MatchOpponents home={home} away={away} stats={stats} mainScore={mainScore} />
                    {!singleOpponent && (
                        <MatchScoreboard
                            score={score}
                            sportId={sportId}
                            server={server}
                            hasScorePlaceholder={hasGameScorePlaceholder(sportId)}
                        />
                    )}
                </div>
                {isVisible && (
                    <MatchBets
                        matchId={matchId}
                        sportName={sportName}
                        otherClass="bg-black-3 tl-br-l tl-br-r"
                        betCount={betCount}
                        status={status}
                    />
                )}
            </MatchDetailBtn>
            <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.LIVE_OVERVIEW }}>
                <CashinMatchCollapsible matchId={matchId} />
            </CashinPositionContext.Provider>
        </div>
    );
});

const MatchFull: FC<{ matchId; sportName: string; active?: boolean }> = memo(({ matchId, sportName, active }) => {
    const derefSub = useReframe();
    const loggedIn = derefSub(loggedIn$);
    const [ref, isVisible] = useSharedIntersectionObserver();
    const match = derefSub(match$$(sportName, matchId));
    const showMatch = derefSub(showMatch$$(sportName, matchId));
    const matchMedia = useMatchMedia();
    const getSportPath = useRouteMatch({ path: TURBO_LIVE_SPORT_PATH, strict: true });
    const { isFavorite, toggleFavorite } = useFavoriteMatch(sportName, match);
    if (!match || !showMatch) {
        return null;
    }
    const isOverview = getSportPath?.isExact;
    const home = localizeMap(match.get('home'));
    const away = localizeMap(match.get('away'));
    const betCount = match.get('betCount');
    const matchTimer = match.get('timer');
    const score = match.get('score');
    const server = score?.get('server');
    const status = matchTimer?.get('status');
    const isRainDelay = isRainDelayExtra(match);
    const stats = match.get('stats');
    const sportId = parseInt(match.get('sportId'));
    const mobileChannel = isMobileChannel();
    const singleOpponent = !match.get('away');
    const statisticsId = match.get('statisticsId');
    const mainScore = showGoalAnimation(sportId) && score?.getIn(['scores', 'TOTAL']);
    const cashinPosition = isOverview ? CashinPosition.LIVE_OVERVIEW : CashinPosition.LIVE_DETAIL_MATCH_LIST;
    return (
        <>
            <div
                ref={ref}
                className={`match match-live match-borders match-overview flex ${
                    active ? 'match-active' : 'bg-white-box'
                }`}
                data-atid={`Match-${matchId}`}
            >
                <div
                    className={clsx('turbo-live-first flex', {
                        'turbo-live-first-wide': matchMedia.x_1680 && !mobileChannel,
                        'turbo-live-first-super-wide': matchMedia.ultra && isOverview && !mobileChannel
                    })}
                >
                    <div className="flex-center tl-br-r">
                        <FavoriteIcon
                            active={isFavorite}
                            toggle={toggleFavorite}
                            iconClass="mx-10 my-15"
                            data-atid="match-favorite-icon"
                        />
                    </div>
                    <MatchDetailBtn matchId={matchId} sportName={sportName} otherClass="flex flex-1 text-left">
                        <div className="match-first flex-col flex-center tl-br-r f-condensed">
                            <MatchTimer
                                scoreboardTypeId={getScoreboardType(match)}
                                matchTimer={match.get('timer')}
                                otherClass="fs-12 c-black-50"
                                shortTimeFormat={isShortTimeFormat(sportName)}
                                mini
                            />
                            {isRainDelay && (
                                <SvgIcon
                                    imgId="real-cloud"
                                    sizeX="14"
                                    sizeY="14"
                                    className="mt-2 c-red"
                                    title={i18n('nui.turbo.live.match.rainDelay')}
                                />
                            )}
                        </div>
                        <MatchOpponents home={home} away={away} stats={stats} mainScore={mainScore} />
                        {isVisible && !singleOpponent && (
                            <MatchScoreboard
                                score={score}
                                sportId={sportId}
                                server={server}
                                hasScorePlaceholder={hasGameScorePlaceholder(sportId)}
                            />
                        )}
                    </MatchDetailBtn>
                    {isVisible && (
                        <>
                            <div className="match-icons-col tl-br-l px-3 flex justify-center">
                                <MatchIconsCols match={match} sportName={sportName} />
                            </div>
                            {loggedIn && (
                                <div className="match-col tl-br-l">
                                    <div className="full-height flex flex-col justify-center items-center">
                                        <TicketsListMatchToggle
                                            matchId={matchId}
                                            usePlaceholder
                                            isOverview={isOverview}
                                        />
                                        <CashinMatchToggle matchId={matchId} usePlaceholder />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {isVisible && <MatchBets sportName={sportName} matchId={matchId} betCount={betCount} status={status} />}
                {isOverview && (
                    <>
                        <div className="turbo-live-last match-last-col pl-3 flex items-center">
                            {statisticsId && (
                                <StatsIcon
                                    statisticsId={statisticsId}
                                    btnClass="btn-no-pad"
                                    iconClass="ml-8 mr-3 match-icon-blue my-10"
                                />
                            )}
                            <MatchBetCount matchId={matchId} sportName={sportName} betCount={betCount} />
                        </div>
                    </>
                )}
            </div>
            <ToggleContainer>
                <CashinPositionContext.Provider value={{ cashinPosition }}>
                    <CashinMatchCollapsible matchId={matchId} />
                    {isOverview && <TicketsMatchCollapsible matchId={matchId} />}
                </CashinPositionContext.Provider>
            </ToggleContainer>
        </>
    );
});

const Match = ({ sportCode, matchId }) => {
    const { matchId: activeMatchId } = useTurboLiveRouteParams();
    return (
        <ErrorBoundary description="TurboLive Match">
            {isMobileChannel() ? (
                <MatchMini matchId={matchId} sportName={sportCode} key={matchId} />
            ) : (
                <MatchFull matchId={matchId} sportName={sportCode} key={matchId} active={matchId === activeMatchId} />
            )}
        </ErrorBoundary>
    );
};

export default Match;
